import type { ChatAnchorInput, ChatRouteId } from '@/types/chatEnvelope';

/**
 * The Study preset group with the student's exam in the questions chip
 * (ORA-952 D3): "Give me NCLEX questions" for a nursing student, whose
 * companion declines a request for USMLE content.
 */
export const getStudyPresetPrompts = (label: string) => ({
  label: 'Study',
  prompts: [
    `Give me ${label} questions`,
    'Give me some flashcards',
    'Quiz me on my weakest topic',
  ],
});

export const PERFORMANCE_PRESET_PROMPTS = {
  label: 'Performance',
  prompts: [
    'What are my best topics?',
    'What are my worst topics?',
    "What's my QBank score?",
  ],
};

export const HELP_PRESET_PROMPTS = {
  label: 'Help',
  prompts: [
    'What is the Daily Session?',
    'What is FSRS?',
    'How Do I Start a QBank?',
  ],
};

/**
 * Which kind of studying the student's primary exam is (ORA-628).
 *
 * `course` is the Preclinical/custom workspace, where the material is the
 * student's own uploaded lectures; `session` is a board exam, where Ora owns
 * the content and paces it as a Daily Session. `unknown` is a surface mounted
 * without the exams slice — it takes the board-exam copy, which is the older
 * and more general of the two.
 */
export type ChatExamMode = 'course' | 'session' | 'unknown';

/**
 * Prompts that use the named app surface rather than generic companion help.
 * Written for a board exam; a Preclinical student sees these except where
 * {@link COURSE_SURFACE_PROMPTS} names the page as different.
 */
export const SURFACE_PROMPTS: Partial<Record<ChatRouteId, readonly string[]>> =
  {
    dashboard: [
      'What should I do today?',
      'How am I tracking for my exam date?',
    ],
    performance: [
      'What do these numbers mean?',
      'Where should I focus this week?',
    ],
    topics: ['Which of these topics am I weakest in?', 'Quiz me on this topic'],
    'qbank.start': [
      'Build a block from my weak topics',
      'What block size should I use?',
    ],
    'flashcard-decks': ['Which deck is most due?', 'How does scheduling work?'],
    'self-assessments': [
      'How do my scores compare?',
      'What should I review from this assessment?',
    ],
    account: ['What does my plan include?', 'How do I change my exam date?'],
    videos: ['What should I watch next?', 'Summarize this video'],
    library: ['What should I read for my weakest topic?'],
  };

/**
 * Course-mode replacements, per route. Only the pages that genuinely differ
 * are here: a Preclinical student has lectures rather than a Daily Session and
 * an exam-date countdown, and their Library reading row is titled "From your
 * lectures". Everything else — topics, QBank, videos, performance — is the
 * same page asking the same questions, so it keeps one set of chips.
 */
const COURSE_SURFACE_PROMPTS: Partial<Record<ChatRouteId, readonly string[]>> =
  {
    dashboard: ['What should I do today?', 'What is left in my lectures?'],
    library: ['What should I read for my lectures?'],
  };

/**
 * The Help group for a course-mode student. Uploading a document, and what Ora
 * does with it afterwards, is the Preclinical workspace's first question and
 * the board-exam groups never mention it.
 *
 * Every chip has to be answerable from the help corpus (`src/help` in ora-be),
 * so these are phrased the way the docs are: "Prepare for Preclinical Exams"
 * carries both "upload a lecture document" and the mapping that follows it.
 */
const COURSE_HELP_PROMPTS = [
  'How do I upload a lecture document?',
  'How does Ora map my documents?',
  // The legacy Help chip, kept verbatim: QBank works the same in either mode.
  'How Do I Start a QBank?',
] as const;

/**
 * The Help chips for this student. Board-exam and unknown-mode surfaces keep
 * the legacy panel's strings exactly (ORA-509).
 */
export const getHelpPrompts = (examMode: ChatExamMode): readonly string[] =>
  examMode === 'course' ? COURSE_HELP_PROMPTS : HELP_PRESET_PROMPTS.prompts;

const anchorPrompts = (anchor: ChatAnchorInput | null): readonly string[] => {
  switch (anchor?.type) {
    case 'article':
      return ['Summarize this article', 'Quiz me on this'];
    case 'question':
      return [
        'Walk me through this question',
        anchor.revealed === true ? 'Explain the answer' : 'Give me a hint',
      ];
    case 'flashcard':
      return ['Explain this card'];
    case 'video':
      return ['What are the key points so far?'];
    case 'lecture':
      return ['What does this lecture cover?'];
    default:
      return [];
  }
};

/**
 * Whether the anchor itself contributes prompts — i.e. the student is looking
 * at a specific piece of content, not just a route. Callers use this to decide
 * whether the generic "study / performance / help" groups are worth showing
 * alongside the page-aware ones.
 */
export const hasAnchorPrompts = (anchor: ChatAnchorInput | null): boolean =>
  anchorPrompts(anchor).length > 0;

/**
 * Prefer prompts about the anchored item, then fill the group with prompts for
 * its route, in the copy that fits the student's exam mode. Three chips keeps
 * the page-aware group compact above the generic fallback groups.
 */
export const getSurfacePrompts = (
  route: ChatRouteId,
  anchor: ChatAnchorInput | null,
  examMode: ChatExamMode = 'unknown'
): readonly string[] => {
  const routePrompts = [
    ...((examMode === 'course' ? COURSE_SURFACE_PROMPTS[route] : undefined) ??
      SURFACE_PROMPTS[route] ??
      []),
  ];

  // A videos landing page has no individual video to summarize.
  if (route === 'videos' && anchor?.type !== 'video') {
    const summarizeIndex = routePrompts.indexOf('Summarize this video');
    if (summarizeIndex >= 0) routePrompts.splice(summarizeIndex, 1);
  }

  return [...new Set([...anchorPrompts(anchor), ...routePrompts])].slice(0, 3);
};
